import { stateMachineMap } from 'packages/task-man/service/state-machine/constants';
import { AnnotationState } from 'packages/lib/api/annotations/contract/enums';
import { Role } from 'packages/task-man/constants';
import { pick } from 'lodash';

/**
 * Describes initial, in-progress, and done states for a given
 * Annotation role (i.e. label | qa | audit)
 */
export type AbstractAnnotationStates = {
  initial: AnnotationState;
  inProgress: AnnotationState;
  done: AnnotationState;
};

const initialStates = (initial: AnnotationState): AbstractAnnotationStates => {
  const inProgress = stateMachineMap.states[initial].ASSIGN;
  if (!inProgress) {
    throw new Error('Undefined in-progress state for provided role.');
  }
  const done = stateMachineMap.states[inProgress].SUBMIT;
  if (!done) {
    throw new Error('Undefined done state for provided role.');
  }
  return { initial, inProgress, done };
};

export const roleStateMap = {
  label: initialStates('ToDo'),
  qa: initialStates('QaToDo'),
  audit: initialStates('AuditToDo'),
  flagged: initialStates('Flagged'),
  error: initialStates('Error'),
};

export const mapRoleToAnnotationStates = (
  role: Role
): AbstractAnnotationStates => {
  const roleStates = roleStateMap[role];
  return roleStates;
};

const roleStateMapEntries = Object.entries(roleStateMap);

/**
 * NOTE:
 * terminal states cannot be used to associate a task with a role,
 * because not all roles have a terminal state.
 *
 * ie. The Label Role has a terminal state of QaToDo.
 */
const getNonTerminalStates = (states: AbstractAnnotationStates) =>
  Object.values(pick(states, ['initial', 'inProgress']));

/**
 * See note on getTerminalStates function definition (fyi):
 *  "terminal states cannot be used to associate a task with a role"
 */
export const mapAnnotationStateToRole = (
  annotationState: AnnotationState
): Role | undefined => {
  const [role] =
    roleStateMapEntries.find(([, states]) =>
      getNonTerminalStates(states).includes(annotationState)
    ) ?? [];
  return role as Role | undefined;
};

/**
 * Returns the initial AnnotationState for the annotation Role associated
 * with the given AnnotationState.
 *
 * Example:
 *   with annotationState === 'AnnotationInProgress'
 *   returns the value 'ToDo'
 *
 * Example:
 *   with annotationState === 'QaInProgress'
 *   returns the value 'QaToDo'
 */
export const getInitialAnnotationState = (annotationState: AnnotationState) => {
  const role = mapAnnotationStateToRole(annotationState);
  if (!role) {
    throw new Error(
      `The AnnotationState ${annotationState} is not associated with a specific labeling role.`
    );
  }
  const { initial } = mapRoleToAnnotationStates(role);
  return initial;
};
